import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";

const STORE_TOKEN_KEY = "alramlaa_store_token";
const STORE_USER_KEY = "alramlaa_store_user";

function loadStoredAuth() {
  try {
    const token = localStorage.getItem(STORE_TOKEN_KEY);
    const userRaw = localStorage.getItem(STORE_USER_KEY);
    const user = userRaw ? JSON.parse(userRaw) : null;
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

function saveAuth(token, user) {
  if (token) {
    localStorage.setItem(STORE_TOKEN_KEY, token);
    if (user) localStorage.setItem(STORE_USER_KEY, JSON.stringify(user));
  }
}

function clearAuth() {
  localStorage.removeItem(STORE_TOKEN_KEY);
  localStorage.removeItem(STORE_USER_KEY);
}

const StoreAuthContext = createContext(null);

export function StoreAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const setAuthHeader = useCallback((authToken) => {
    if (authToken) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${authToken}`;
    } else {
      delete axios.defaults.headers.common["Authorization"];
    }
  }, []);

  const loginWithGoogle = useCallback(
    async (idToken) => {
      try {
        const { data } = await axios.post("/auth/google", { idToken });
        setToken(data.token);
        setUser(data.user);
        setAuthHeader(data.token);
        saveAuth(data.token, data.user);
        return { success: true };
      } catch (err) {
        const msg =
          err.response?.data?.message || "Google sign-in failed.";
        return { success: false, message: msg };
      }
    },
    [setAuthHeader]
  );

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setAuthHeader(null);
    clearAuth();
  }, [setAuthHeader]);

  const refreshUser = useCallback(async () => {
    const authToken =
      token ||
      (typeof localStorage !== "undefined"
        ? localStorage.getItem(STORE_TOKEN_KEY)
        : null);
    if (!authToken) return null;
    try {
      const { data } = await axios.get("/auth/me", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUser(data.user);
      saveAuth(authToken, data.user);
      setAuthHeader(authToken);
      return data.user;
    } catch {
      return null;
    }
  }, [token, setAuthHeader]);

  // Restore session from storage and validate with backend
  useEffect(() => {
    const { token: storedToken, user: storedUser } = loadStoredAuth();
    if (!storedToken) {
      setLoading(false);
      return;
    }
    setAuthHeader(storedToken);
    setToken(storedToken);
    setUser(storedUser);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    axios
      .get("/auth/me", { signal: controller.signal, timeout: 10000 })
      .then(({ data }) => {
        setUser(data.user);
        saveAuth(storedToken, data.user);
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      isAuthenticated: !!user && !!token,
      loginWithGoogle,
      logout,
      refreshUser,
    }),
    [
      user,
      token,
      loading,
      loginWithGoogle,
      logout,
      refreshUser,
    ]
  );

  return (
    <StoreAuthContext.Provider value={value}>
      {children}
    </StoreAuthContext.Provider>
  );
}

export function useStoreAuth() {
  const ctx = useContext(StoreAuthContext);
  if (!ctx) {
    throw new Error("useStoreAuth must be used within StoreAuthProvider");
  }
  return ctx;
}
